import { AccountService } from '@fp/interfaces/conversational/generated/api/account.service';
import { ConfigResponse } from '@fp/interfaces/conversational/generated/model/config-response';
import { ConversationModel } from '@klant/conversational-connector/models/Conversation.model';
import { ConversationService } from '@klant/conversational-connector/services/conversation/conversation.service';

import { inject, Injectable } from '@angular/core';
import { Observable, switchMap } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';

import { SessionStorageService } from '../../session-storage/session-storage.service';
import { ConversationalConfigStore } from '../../signal-stores/conversational-config-store/conversational-config.store';

import { ChatSession, LoginStatus, SeamlyAPIContext } from './klant-conversational-connector.model';

@Injectable({ providedIn: 'root' })
export class KlantConversationalConnectorService {
    private readonly conversationalConfigStore = inject(ConversationalConfigStore);
    private readonly accountService = inject(AccountService);
    private readonly conversationService = inject(ConversationService);
    private readonly sessionStorageService = inject(SessionStorageService);

    public initializeSeamly(chatSession: ChatSession): Observable<ConversationModel> {
        const isChatAlreadyStartedInThisSession: boolean = !!this.sessionStorageService.getConversationSessionDetails();

        if (isChatAlreadyStartedInThisSession && chatSession.loginStatus === LoginStatus.LOGGED_IN) {
            return this.resumeConversation(chatSession);
        }

        return this.createConversation(chatSession);
    }

    private resumeConversation(chatSession: ChatSession): Observable<ConversationModel> {
        return this.conversationService.getConversation().pipe(
            catchError((error) => {
                if (error && error.status === 404) {
                    return this.createConversation(chatSession);
                }
                throw error;
            })
        );
    }

    private createConversation(chatSession: ChatSession): Observable<ConversationModel> {
        const seamlyConfiguration = this.conversationalConfigStore.seamlyConfiguration();
        if (!seamlyConfiguration) {
            throw new Error('Seamly configuration is not available');
        }

        this.sessionStorageService.updateSeamlyEnvironmentConfig({
            seamlyEnvironment: seamlyConfiguration.environment,
            seamlyApiDomain: seamlyConfiguration.seamlyApiDomain
        });

        return this.getConversationLink(seamlyConfiguration.seamlyApiKey).pipe(
            switchMap((conversationLink: string) => {
                return this.conversationService.createConversation(conversationLink, chatSession);
            }),
            tap((conversation: ConversationModel) => {
                if (chatSession.loginStatus === LoginStatus.LOGGED_IN) {
                    this.sessionStorageService.updateConversationSessionDetails({
                        conversationLink: conversation.conversationLink,
                        accessToken: conversation.accessToken
                    });
                }
            })
        );
    }

    private getConversationLink(apiKey: string): Observable<string> {
        return this.accountService
            .getAccountConfig({
                apiKey,
                getAccountConfigRequest: SeamlyAPIContext
            })
            .pipe(
                map((configResponse: ConfigResponse) => {
                    return configResponse._links.conversations?.href as string;
                })
            );
    }
}
